import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  onSnapshot,
  doc,
  getDoc,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { Autocomplete } from "@react-google-maps/api";

import {
  JOB_TYPES,
  normalizeJobType,
  dropLegacy,
  saveJobTypes,
  ensureJobsForRuns,
} from "../../services/jobTypes";
import SearchableSelect from "./editJobs/SearchableSelect";
import LiveSearchSelect from "./editJobs/LiveSearchSelect";
import {
  getCompanyUiVariant,
  firstNonEmpty,
  getSiteContactName,
  getSiteContactPhone,
  getSiteContactEmail,
  getTruckDisplayLabel,
  getDriverDisplayLabel,
  makeAssignmentDocIds,
  getUniqueRunKey,
  sortRunEntries,
  sortRunsObject,
  moveAllPerRunScopedFields,
  getRunVehicleMeta,
  formatRunVehicle,
  getRunCompanyId,
  getNextRunKey,
  removePerRunScopedFields,
} from "./editJobs/runHelpers";

const labelClass =
  "mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500";

const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm transition focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";

const filledCardClass = "rounded-2xl border border-gray-200 bg-gray-50/80 p-4";

export default function EditJobsTab({ drivers, scriptLoaded }) {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedDriver, setSelectedDriver] = useState("");
  const [selectedDate, setSelectedDate] = useState(() =>
    new Date().toLocaleDateString("en-CA", {
      timeZone: "Australia/Melbourne",
    }),
  );

  const [customers, setCustomers] = useState([]);
  const [companyJobTypes, setCompanyJobTypes] = useState([]);
  const [companyTrucks, setCompanyTrucks] = useState([]);
  const [customersByCompany, setCustomersByCompany] = useState({});
  const [jobTypesByCompany, setJobTypesByCompany] = useState({});
  const [trucksByCompany, setTrucksByCompany] = useState({});
  const [assignment, setAssignment] = useState(null);
  const [assignmentDocId, setAssignmentDocId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [movingAllRuns, setMovingAllRuns] = useState(false);
  const [moveAllTargetDriver, setMoveAllTargetDriver] = useState("");
  const [openJobKeys, setOpenJobKeys] = useState(() => new Set());
  const autocompleteRefs = useRef({});

  // Job cards are keyed by array index, which shifts on insert/move/delete.
  // Rather than try to carry the "open" state to the right job across a
  // reorder, just collapse everything back to the default on any structural
  // change - simpler and can never show the wrong job as expanded.
  const resetOpenJobKeys = () => setOpenJobKeys(new Set());

  const toggleJobOpen = (runKey, realIndex) => {
    const key = `${runKey}__${realIndex}`;
    setOpenJobKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedCompanyObj = useMemo(
    () => companies.find((c) => c.id === selectedCompany) || null,
    [companies, selectedCompany],
  );

  const selectedCompanyUiVariant = useMemo(
    () => getCompanyUiVariant(selectedCompanyObj),
    [selectedCompanyObj],
  );

  const companyNameById = useMemo(() => {
    const map = {};
    companies.forEach((company) => {
      map[company.id] = company.name || company.id;
    });
    return map;
  }, [companies]);

  const driverOptions = useMemo(
    () =>
      (drivers || []).map((driver) => {
        const companyIds = [
          driver.companyId,
          ...(Array.isArray(driver.companyIds) ? driver.companyIds : []),
        ].filter(Boolean);
        const companyNames = companyIds
          .map((companyId) => companyNameById[companyId] || companyId)
          .filter(Boolean);

        return {
          value: driver.uid,
          label: getDriverDisplayLabel(driver),
          description: companyNames.join(" | "),
          searchText: [
            getDriverDisplayLabel(driver),
            driver.uid,
            driver.email,
            driver.name,
            driver.displayName,
            ...companyIds,
            ...companyNames,
          ]
            .filter(Boolean)
            .join(" "),
        };
      }),
    [companyNameById, drivers],
  );

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "companies"), (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => String(a.name || "").localeCompare(b.name || ""));
      setCompanies(list);
      if (!selectedCompany && list.length) setSelectedCompany(list[0].id);
    });
    return () => unsub();
  }, [selectedCompany]);

  useEffect(() => {
    if (!selectedCompany) {
      setCustomers([]);
      setCompanyJobTypes([]);
      setCompanyTrucks([]);
      return undefined;
    }

    const unsubs = [];

    const unCustomers = onSnapshot(
      query(
        collection(db, "customers"),
        where("companyId", "==", selectedCompany),
      ),
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.name || "").localeCompare(b.name || ""));
        setCustomers(list);
        setCustomersByCompany((prev) => ({ ...prev, [selectedCompany]: list }));
      },
    );
    unsubs.push(unCustomers);

    const unJobTypes = onSnapshot(
      query(
        collection(db, "jobTypes"),
        where("companyId", "==", selectedCompany),
      ),
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.name || "").localeCompare(b.name || ""));
        setCompanyJobTypes(list);
        setJobTypesByCompany((prev) => ({ ...prev, [selectedCompany]: list }));
      },
    );
    unsubs.push(unJobTypes);

    const unTrucks = onSnapshot(
      query(
        collection(db, "trucks"),
        where("companyId", "==", selectedCompany),
      ),
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((t) => t.active !== false)
          .sort((a, b) => String(a.rego || "").localeCompare(String(b.rego || "")));
        setCompanyTrucks(list);
        setTrucksByCompany((prev) => ({ ...prev, [selectedCompany]: list }));
      },
    );
    unsubs.push(unTrucks);

    return () => unsubs.forEach((unsub) => unsub && unsub());
  }, [selectedCompany]);

  useEffect(() => {
    const relevantCompanyIds = Array.from(
      new Set(
        [
          selectedCompany,
          ...Object.keys(assignment?.runMeta || {}).map((runKey) =>
            firstNonEmpty(assignment?.runMeta?.[runKey]?.companyId),
          ),
          ...Object.entries(assignment?.runs || {}).map(([runKey, jobs]) =>
            getRunCompanyId(assignment, runKey, jobs),
          ),
        ].filter((companyId) => companyId && companyId !== "mixed"),
      ),
    );

    if (!relevantCompanyIds.length) return undefined;

    const unsubs = [];

    relevantCompanyIds.forEach((companyId) => {
      if (companyId === selectedCompany) return;

      if (!customersByCompany[companyId]) {
        unsubs.push(
          onSnapshot(
            query(collection(db, "customers"), where("companyId", "==", companyId)),
            (snap) => {
              const list = snap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .sort((a, b) => String(a.name || "").localeCompare(b.name || ""));
              setCustomersByCompany((prev) => ({ ...prev, [companyId]: list }));
            },
          ),
        );
      }

      if (!jobTypesByCompany[companyId]) {
        unsubs.push(
          onSnapshot(
            query(collection(db, "jobTypes"), where("companyId", "==", companyId)),
            (snap) => {
              const list = snap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .sort((a, b) => String(a.name || "").localeCompare(b.name || ""));
              setJobTypesByCompany((prev) => ({ ...prev, [companyId]: list }));
            },
          ),
        );
      }

      if (!trucksByCompany[companyId]) {
        unsubs.push(
          onSnapshot(
            query(collection(db, "trucks"), where("companyId", "==", companyId)),
            (snap) => {
              const list = snap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .filter((t) => t.active !== false)
                .sort((a, b) => String(a.rego || "").localeCompare(String(b.rego || "")));
              setTrucksByCompany((prev) => ({ ...prev, [companyId]: list }));
            },
          ),
        );
      }
    });

    return () => unsubs.forEach((unsub) => unsub && unsub());
  }, [assignment, selectedCompany, customersByCompany, jobTypesByCompany, trucksByCompany]);

  const blankJob = () => ({
    customerId: "",
    siteId: "",
    siteLabel: "",
    address: "",
    eta: "",
    description: "",
    poNumber: "",
    epaWasteTracker: "",
    siteContact: "",
    sitePhone: "",
    siteEmail: "",
    jobTypeId: "",
    jobTypeCode: "",
    jobTypeName: "",
    assignedSource: "default",
  });

  const isGreenspaceRow = (item, company = null) => {
    if (!item) return false;
    if (item.jobTypeId) return true;
    if (item.uiVariant === "greenspace") return true;
    if (getCompanyUiVariant(company) === "greenspace") return true;
    if (selectedCompanyUiVariant === "greenspace") return true;
    return false;
  };

  const hydrateLoadedJob = (item) => {
    if (!item || item.isWeighbridgeDocket) return item;

    const row = { ...item };

    if (row.jobTypeId) {
      const jt = companyJobTypes.find((j) => j.id === row.jobTypeId);
      if (jt) {
        row.jobTypeCode = row.jobTypeCode || jt.code || "";
        row.jobTypeName = row.jobTypeName || jt.name || "";
      }
      return row;
    }

    if (row.jobTypeCode || row.jobTypeName) {
      return row;
    }

    if (
      row.assignedSource === "default" ||
      row.uiVariant === "default" ||
      (!row.jobTypeId && !row.jobTypeCode && !row.jobTypeName)
    ) {
      return {
        ...row,
        jobTypeId: row.jobTypeId || "",
        jobTypeCode: "",
        jobTypeName: "",
        assignedSource: row.assignedSource || "default",
      };
    }

    return normalizeJobType(row);
  };

  const fetchAssignment = async () => {
    if (!selectedDriver || !selectedDate) return;
    resetOpenJobKeys();
    setLoading(true);
    try {
      const { scopedId, legacyId } = makeAssignmentDocIds(
        selectedCompany,
        selectedDriver,
        selectedDate,
      );

      let snap = null;
      let foundDocId = null;

      if (scopedId) {
        snap = await getDoc(doc(db, "assignments", scopedId));
        if (snap.exists()) foundDocId = scopedId;
      }

      if ((!snap || !snap.exists()) && legacyId) {
        snap = await getDoc(doc(db, "assignments", legacyId));
        if (snap.exists()) foundDocId = legacyId;
      }

      if (snap && snap.exists()) {
        const data = snap.data() || {};
        const runs = {};
        for (const [rk, arr] of Object.entries(data.runs || {})) {
          runs[rk] = (arr || []).map((j) => hydrateLoadedJob(j));
        }
        setAssignment({ ...data, runs: sortRunsObject(runs) });
        setAssignmentDocId(foundDocId);
      } else {
        setAssignment({
          companyId: selectedCompany || "",
          driverId: selectedDriver,
          date: selectedDate,
          runs: { "Run #1": [] },
        });
        setAssignmentDocId(scopedId || legacyId);
      }

      setMoveAllTargetDriver("");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (runKey, index, field, value) => {
    setAssignment((prev) => {
      const updated = { ...(prev || {}), runs: { ...(prev?.runs || {}) } };
      const runArr = [...(updated.runs[runKey] || [])];
      const item = { ...runArr[index] };

      if (!item) return prev;

      if (item.isWeighbridgeDocket) {
        runArr[index] = item;
        updated.runs[runKey] = runArr;
        updated.runs = sortRunsObject(updated.runs);
        return updated;
      }

      if (field === "jobTypeCode") {
        if (!value) {
          item.jobTypeCode = "";
          item.jobTypeName = "";
          if (item.jobTypeId) item.jobTypeId = "";
          item.assignedSource = "default";
        } else {
          const jt = JOB_TYPES.find((j) => j.code === value);
          item.jobTypeCode = value;
          item.jobTypeName = jt?.name || "";
          item.jobTypeId = "";
          if (item.assignedSource === "default") delete item.assignedSource;
          Object.assign(item, dropLegacy(item));
        }
      } else if (field === "jobTypeId") {
        if (!value) {
          item.jobTypeId = "";
          item.jobTypeCode = "";
          item.jobTypeName = "";
          item.assignedSource = "default";
        } else {
          const jt = companyJobTypes.find((j) => j.id === value);
          item.jobTypeId = value;
          item.jobTypeCode = jt?.code || "";
          item.jobTypeName = jt?.name || "";
          if (item.assignedSource === "default") delete item.assignedSource;
        }
      } else {
        item[field] = value;
      }

      runArr[index] = item;
      updated.runs[runKey] = runArr;
      updated.runs = sortRunsObject(updated.runs);
      return updated;
    });
  };

  const addJobToEnd = (runKey) => {
    resetOpenJobKeys();
    setAssignment((prev) => {
      const updated = { ...(prev || {}), runs: { ...(prev?.runs || {}) } };
      const arr = [...(updated.runs[runKey] || [])];
      const docketIndex = arr.findIndex((j) => j?.isWeighbridgeDocket);
      if (docketIndex === -1) {
        arr.push(blankJob());
      } else {
        arr.splice(docketIndex, 0, blankJob());
      }
      updated.runs[runKey] = arr;
      updated.runs = sortRunsObject(updated.runs);
      return updated;
    });
  };

  const insertJobAt = (runKey, index) => {
    resetOpenJobKeys();
    setAssignment((prev) => {
      const updated = { ...(prev || {}), runs: { ...(prev?.runs || {}) } };
      const clone = [...(updated.runs[runKey] || [])];
      clone.splice(index, 0, blankJob());
      updated.runs[runKey] = clone;
      updated.runs = sortRunsObject(updated.runs);
      return updated;
    });
  };

  const moveJob = (runKey, visibleIndex, dir) => {
    resetOpenJobKeys();
    setAssignment((prev) => {
      const runs = prev?.runs || {};
      const arr = [...(runs[runKey] || [])];
      const visibleIndices = [];
      arr.forEach((item, idx) => {
        if (!item?.isWeighbridgeDocket) visibleIndices.push(idx);
      });

      if (!visibleIndices.length) return prev;

      const toVisible = dir === "up" ? visibleIndex - 1 : visibleIndex + 1;
      if (toVisible < 0 || toVisible >= visibleIndices.length) return prev;

      const fromReal = visibleIndices[visibleIndex];
      const toReal = visibleIndices[toVisible];

      const swapArr = [...arr];
      [swapArr[fromReal], swapArr[toReal]] = [
        swapArr[toReal],
        swapArr[fromReal],
      ];

      return {
        ...prev,
        runs: sortRunsObject({ ...runs, [runKey]: swapArr }),
      };
    });
  };

  const deleteJob = (runKey, index) => {
    resetOpenJobKeys();
    setAssignment((prev) => {
      const updated = { ...(prev || {}), runs: { ...(prev?.runs || {}) } };
      const clone = [...(updated.runs[runKey] || [])];
      const item = clone[index];
      if (item?.isWeighbridgeDocket) return prev;
      clone.splice(index, 1);
      updated.runs[runKey] = clone;
      updated.runs = sortRunsObject(updated.runs);
      return updated;
    });
  };

  const addRun = () => {
    setAssignment((prev) => {
      const nextRunKey = getNextRunKey(prev?.runs || {});
      return {
        ...(prev || {}),
        companyId: selectedCompany || prev?.companyId || "",
        driverId: selectedDriver || prev?.driverId || "",
        date: selectedDate || prev?.date || "",
        runs: sortRunsObject({
          ...(prev?.runs || {}),
          [nextRunKey]: [],
        }),
        runMeta: {
          ...(prev?.runMeta || {}),
          [nextRunKey]: prev?.runMeta?.[nextRunKey] || {},
        },
      };
    });
  };

  const deleteRun = (runKey) => {
    setAssignment((prev) => {
      const currentRuns = { ...(prev?.runs || {}) };
      const runKeys = Object.keys(currentRuns);
      if (!runKeys.includes(runKey)) return prev;
      if (runKeys.length <= 1) {
        alert("At least one run must remain.");
        return prev;
      }

      delete currentRuns[runKey];
      const nextRunMeta = { ...(prev?.runMeta || {}) };
      delete nextRunMeta[runKey];

      const cleaned = removePerRunScopedFields(
        {
          ...(prev || {}),
          runs: currentRuns,
          runMeta: nextRunMeta,
        },
        runKey,
      );

      return {
        ...cleaned,
        runs: sortRunsObject(cleaned.runs || {}),
        runMeta: cleaned.runMeta || {},
      };
    });
  };

  const setRunCompany = (runKey, companyId) => {
    const company = companies.find((c) => c.id === companyId) || null;
    const uiVariant = getCompanyUiVariant(company);

    setAssignment((prev) => {
      const nextRunMeta = { ...(prev?.runMeta || {}) };
      nextRunMeta[runKey] = {
        ...(nextRunMeta[runKey] || {}),
        companyId: companyId || "",
        companyName: company?.name || "",
        uiVariant,
        vehicleId: "",
        unitType: "",
        vehicleType: "",
        truckType: "",
        rego: "",
        vehicleRego: "",
        truckRego: "",
        vehicleLabel: "",
        vehicleName: "",
        truckLabel: "",
        truckName: "",
        vehicleLoadType: "",
        loadType: "",
      };

      const nextRuns = { ...(prev?.runs || {}) };
      nextRuns[runKey] = (nextRuns[runKey] || []).map((job) => {
        if (!job || job.isWeighbridgeDocket) return job;
        return {
          ...job,
          companyId: companyId || "",
          companyName: company?.name || "",
          uiVariant,
          customerId: "",
          customerName: "",
          siteId: "",
          siteLabel: "",
          jobTypeId: "",
          jobTypeCode: "",
          jobTypeName: "",
          vehicleId: "",
          unitType: "",
          vehicleType: "",
          truckType: "",
          rego: "",
          vehicleRego: "",
          truckRego: "",
          vehicleLabel: "",
          vehicleName: "",
          truckLabel: "",
          truckName: "",
          vehicleLoadType: "",
          loadType: "",
          assignedSource: uiVariant === "greenspace" ? "mixed-assign" : "default",
        };
      });

      return {
        ...(prev || {}),
        runs: sortRunsObject(nextRuns),
        runMeta: nextRunMeta,
      };
    });
  };

  const setRunVehicleMeta = (runKey, companyId, vehicleId) => {
    const runTrucks =
      trucksByCompany[companyId] ||
      (companyId === selectedCompany ? companyTrucks : []) || [];
    const selectedTruck = runTrucks.find((truck) => truck.id === vehicleId) || null;

    setAssignment((prev) => {
      const nextRunMeta = { ...(prev?.runMeta || {}) };
      const existingMeta = { ...(nextRunMeta[runKey] || {}) };

      if (!vehicleId || !selectedTruck) {
        nextRunMeta[runKey] = {
          ...existingMeta,
          vehicleId: "",
          unitType: "",
          vehicleType: "",
          truckType: "",
          rego: "",
          vehicleRego: "",
          truckRego: "",
          vehicleLabel: "",
          vehicleName: "",
          truckLabel: "",
          truckName: "",
          vehicleLoadType: "",
          loadType: "",
        };
      } else {
        const rego = firstNonEmpty(
          selectedTruck.rego,
          selectedTruck.vehicleRego,
          selectedTruck.truckRego,
        );
        const unitType = firstNonEmpty(
          selectedTruck.loadType,
          selectedTruck.unitType,
          selectedTruck.vehicleType,
          selectedTruck.truckType,
          selectedTruck.type,
        );
        const label = getTruckDisplayLabel(selectedTruck);

        nextRunMeta[runKey] = {
          ...existingMeta,
          vehicleId,
          unitType,
          vehicleType: unitType,
          truckType: unitType,
          rego,
          vehicleRego: rego,
          truckRego: rego,
          vehicleLabel: label,
          vehicleName: label,
          truckLabel: label,
          truckName: label,
          vehicleLoadType: unitType,
          loadType: unitType,
        };
      }

      const nextRuns = { ...(prev?.runs || {}) };
      nextRuns[runKey] = (nextRuns[runKey] || []).map((job) => {
        if (!job || job.isWeighbridgeDocket) return job;
        const meta = nextRunMeta[runKey] || {};
        return {
          ...job,
          vehicleId: meta.vehicleId || "",
          unitType: meta.unitType || "",
          vehicleType: meta.vehicleType || "",
          truckType: meta.truckType || "",
          rego: meta.rego || "",
          vehicleRego: meta.vehicleRego || "",
          truckRego: meta.truckRego || "",
          vehicleLabel: meta.vehicleLabel || "",
          vehicleName: meta.vehicleName || "",
          truckLabel: meta.truckLabel || "",
          truckName: meta.truckName || "",
          vehicleLoadType: meta.vehicleLoadType || "",
          loadType: meta.loadType || "",
        };
      });

      return {
        ...(prev || {}),
        runMeta: nextRunMeta,
        runs: sortRunsObject(nextRuns),
      };
    });
  };

  const saveChanges = async () => {
    if (!assignment || !selectedDriver || !selectedDate) return;

    const originalRuns = assignment.runs || {};
    const toSave = JSON.parse(JSON.stringify(originalRuns));

    Object.entries(toSave).forEach(([runKey, arr]) => {
      const runCompanyId = getRunCompanyId(assignment, runKey, arr || []);
      const runJobTypes =
        jobTypesByCompany[runCompanyId] ||
        (runCompanyId === selectedCompany ? companyJobTypes : []) || [];

      (arr || []).forEach((it) => {
        if (it?.isWeighbridgeDocket) return;

        if (runCompanyId) {
          const runCompany = companies.find((c) => c.id === runCompanyId) || null;
          it.companyId = runCompanyId;
          it.companyName = runCompany?.name || it.companyName || "";
          it.uiVariant = getCompanyUiVariant(runCompany);
        }

        if (it.jobTypeId) {
          const jt = runJobTypes.find((j) => j.id === it.jobTypeId);
          if (jt) {
            it.jobTypeCode = jt.code || it.jobTypeCode || "";
            it.jobTypeName = jt.name || it.jobTypeName || "";
          }
        }

        const hasType = !!it.jobTypeCode || !!it.jobTypeName || !!it.jobTypeId;
        if (!hasType) {
          it.jobTypeCode = "";
          it.jobTypeName = "";
          it.assignedSource = getCompanyUiVariant(
            companies.find((c) => c.id === runCompanyId) || null,
          ) === "greenspace"
            ? "mixed-assign"
            : "default";
        }
      });
    });

    const baseCompanyId =
      selectedCompany ||
      (assignment.companyId && assignment.companyId !== "mixed"
        ? assignment.companyId
        : "");

    const runsWithJobs = await ensureJobsForRuns({
      companyId: baseCompanyId,
      driverId: selectedDriver,
      date: selectedDate,
      runs: toSave,
    });

    const mergedRuns = {};
    const allRunKeys = new Set([
      ...Object.keys(originalRuns || {}),
      ...Object.keys(runsWithJobs || {}),
    ]);

    for (const runKey of allRunKeys) {
      const newArr = (runsWithJobs && runsWithJobs[runKey]) || [];
      const origArr = (originalRuns && originalRuns[runKey]) || [];
      const mergedArr = newArr.map((item, idx) => {
        const orig = origArr[idx] || {};
        return { ...orig, ...item };
      });
      mergedRuns[runKey] = mergedArr;
    }

    await saveJobTypes({
      companyId: baseCompanyId,
      driverId: selectedDriver,
      date: selectedDate,
      runs: sortRunsObject(mergedRuns),
      otherMerge: {
        companyId: baseCompanyId,
        driverId: selectedDriver,
        date: selectedDate,
        runMeta: assignment?.runMeta || {},
      },
    });

    alert("Saved successfully");
  };

  const moveAllRunsToDriver = async () => {
    const targetDriverId = moveAllTargetDriver || "";
    const baseCompanyId = selectedCompany || assignment?.companyId || "";

    if (!assignment || !selectedDriver || !selectedDate) return;
    if (!targetDriverId) {
      alert("Please select the driver to move all runs to.");
      return;
    }
    if (targetDriverId === selectedDriver) {
      alert("Please select a different driver.");
      return;
    }

    const sourceRunEntries = sortRunEntries(assignment.runs || {});
    if (!sourceRunEntries.length) {
      alert("There are no runs to move.");
      return;
    }

    setMovingAllRuns(true);
    try {
      const sourceIds = makeAssignmentDocIds(
        baseCompanyId,
        selectedDriver,
        selectedDate,
      );
      const targetIds = makeAssignmentDocIds(
        baseCompanyId,
        targetDriverId,
        selectedDate,
      );

      let sourceDocId =
        assignmentDocId || sourceIds.scopedId || sourceIds.legacyId;
      let sourceSnap = null;

      if (sourceDocId) {
        sourceSnap = await getDoc(doc(db, "assignments", sourceDocId));
      }

      if (!sourceSnap || !sourceSnap.exists()) {
        if (sourceIds.scopedId) {
          const tryScoped = await getDoc(
            doc(db, "assignments", sourceIds.scopedId),
          );
          if (tryScoped.exists()) {
            sourceSnap = tryScoped;
            sourceDocId = sourceIds.scopedId;
          }
        }
        if ((!sourceSnap || !sourceSnap.exists()) && sourceIds.legacyId) {
          const tryLegacy = await getDoc(
            doc(db, "assignments", sourceIds.legacyId),
          );
          if (tryLegacy.exists()) {
            sourceSnap = tryLegacy;
            sourceDocId = sourceIds.legacyId;
          }
        }
      }

      const sourceData = sourceSnap?.exists()
        ? sourceSnap.data()
        : {
            companyId: baseCompanyId,
            driverId: selectedDriver,
            date: selectedDate,
            runs: assignment.runs || {},
          };

      let targetSnap = null;
      let targetDocId = null;

      if (targetIds.scopedId) {
        const tryScoped = await getDoc(
          doc(db, "assignments", targetIds.scopedId),
        );
        if (tryScoped.exists()) {
          targetSnap = tryScoped;
          targetDocId = targetIds.scopedId;
        }
      }

      if ((!targetSnap || !targetSnap.exists()) && targetIds.legacyId) {
        const tryLegacy = await getDoc(
          doc(db, "assignments", targetIds.legacyId),
        );
        if (tryLegacy.exists()) {
          targetSnap = tryLegacy;
          targetDocId = targetIds.legacyId;
        }
      }

      if (!targetDocId) {
        targetDocId = targetIds.scopedId || targetIds.legacyId;
      }

      const targetData = targetSnap?.exists()
        ? targetSnap.data()
        : {
            companyId: baseCompanyId,
            driverId: targetDriverId,
            date: selectedDate,
            runs: {},
          };

      const sourceRuns = { ...(sourceData.runs || {}) };
      const targetRuns = { ...(targetData.runs || {}) };
      const runKeyMap = {};

      for (const [runKey] of sourceRunEntries) {
        if (!sourceRuns[runKey]) continue;

        const targetRunKey = getUniqueRunKey(targetRuns, runKey);
        targetRuns[targetRunKey] = sourceRuns[runKey];
        delete sourceRuns[runKey];
        runKeyMap[runKey] = targetRunKey;
      }

      let nextSourceData = {
        ...sourceData,
        companyId: baseCompanyId,
        driverId: selectedDriver,
        date: selectedDate,
        runs: sortRunsObject(sourceRuns),
      };

      let nextTargetData = {
        ...targetData,
        companyId: baseCompanyId,
        driverId: targetDriverId,
        date: selectedDate,
        runs: sortRunsObject(targetRuns),
      };

      const movedScoped = moveAllPerRunScopedFields(
        nextSourceData,
        nextTargetData,
        runKeyMap,
      );
      nextSourceData = {
        ...movedScoped.nextSource,
        runs: sortRunsObject(movedScoped.nextSource.runs || {}),
      };
      nextTargetData = {
        ...movedScoped.nextTarget,
        runs: sortRunsObject(movedScoped.nextTarget.runs || {}),
      };

      const batch = writeBatch(db);
      batch.set(doc(db, "assignments", sourceDocId), nextSourceData, {
        merge: true,
      });
      batch.set(doc(db, "assignments", targetDocId), nextTargetData, {
        merge: true,
      });
      await batch.commit();

      setAssignment((prev) => ({
        ...(prev || {}),
        runs: {},
      }));

      setMoveAllTargetDriver("");

      const movedCount = Object.keys(runKeyMap).length;
      alert(
        movedCount === 1
          ? "1 run moved successfully."
          : `${movedCount} runs moved successfully.`,
      );
    } catch (error) {
      console.error("Failed to move all runs:", error);
      alert(`Failed to move all runs: ${error.message || "Unknown error"}`);
    } finally {
      setMovingAllRuns(false);
    }
  };

  const sortedRunEntries = useMemo(
    () => sortRunEntries(assignment?.runs || {}),
    [assignment],
  );

  return (
    <div className="w-full px-4 py-4 sm:px-6 sm:py-6 xl:px-8 space-y-6 text-gray-900">
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="px-5 py-5 sm:px-6">
          <div className="grid grid-cols-1 items-end gap-4 xl:grid-cols-[1fr_1fr_1fr_auto]">
            <label className="block">
              <div className={labelClass}>Company</div>
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className={inputClass}
              >
                <option value="">Legacy / unscoped</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <div className={labelClass}>Driver</div>
              <LiveSearchSelect
                value={selectedDriver}
                onChange={setSelectedDriver}
                options={driverOptions}
                placeholder="Search drivers..."
                emptyText="No drivers found."
                className="w-full"
              />
            </label>

            <label className="block">
              <div className={labelClass}>Date</div>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className={inputClass}
              />
            </label>

            <div className="flex flex-wrap gap-2 xl:justify-end">
              <button
                onClick={fetchAssignment}
                className="rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-black"
              >
                Load jobs
              </button>

              {assignment && (
                <>
                  <button
                    onClick={addRun}
                    className="rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-black"
                  >
                    Add run
                  </button>
                  <button
                    onClick={saveChanges}
                    className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700"
                  >
                    Save changes
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <p className="text-sm text-gray-600">Loading assignment...</p>
      )}

      {!loading && assignment && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h3 className="text-base font-semibold text-amber-900">
                  Move all runs for this driver and date
                </h3>
                <p className="text-sm text-amber-800">
                  This transfers the whole day’s runs, not just one run card.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                <LiveSearchSelect
                  value={moveAllTargetDriver}
                  onChange={setMoveAllTargetDriver}
                  options={driverOptions.filter(
                    (option) => option.value !== selectedDriver,
                  )}
                  placeholder="Move all runs to driver..."
                  emptyText="No other drivers found."
                  className="min-w-[240px]"
                />

                <button
                  onClick={moveAllRunsToDriver}
                  disabled={movingAllRuns}
                  className={`rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                    movingAllRuns
                      ? "cursor-not-allowed bg-gray-300 text-gray-600"
                      : "bg-amber-500 text-white hover:bg-amber-600"
                  }`}
                >
                  {movingAllRuns ? "Moving all runs..." : "Move all runs"}
                </button>
              </div>
            </div>
          </div>

          {sortedRunEntries.map(([runKey, jobs]) => {
            const visibleRealIndices = [];
            jobs.forEach((j, idx) => {
              if (!j?.isWeighbridgeDocket) visibleRealIndices.push(idx);
            });

            const runCompanyId = getRunCompanyId(assignment, runKey, jobs);
            const runCompany = companies.find((c) => c.id === runCompanyId) || null;
            const runUiVariant = getCompanyUiVariant(runCompany);
            const runCustomers =
              customersByCompany[runCompanyId] ||
              (runCompanyId === selectedCompany ? customers : []) || [];
            const runJobTypes =
              jobTypesByCompany[runCompanyId] ||
              (runCompanyId === selectedCompany ? companyJobTypes : []) || [];
            const runTrucks =
              trucksByCompany[runCompanyId] ||
              (runCompanyId === selectedCompany ? companyTrucks : []) || [];
            const runVehicle = getRunVehicleMeta(assignment, runKey, jobs);

            return (
              <div
                key={runKey}
                className="space-y-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                  <div className="space-y-2">
                    <div>
                      <h3 className="text-lg font-semibold">{runKey}</h3>
                      <p className="text-xs text-gray-500">
                        Reorder, insert or edit jobs in this run.
                      </p>
                    </div>

                    <div className="inline-flex flex-wrap items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
                      <span className="font-medium text-emerald-800">
                        Assigned vehicle:
                      </span>
                      <span
                        className={
                          runVehicle.hasVehicle
                            ? "font-semibold text-emerald-900"
                            : "text-emerald-700"
                        }
                      >
                        {formatRunVehicle(runVehicle)}
                      </span>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      <label className="block">
                        <div className={labelClass}>Company for this run</div>
                        <select
                          value={runCompanyId || ""}
                          onChange={(e) => setRunCompany(runKey, e.target.value)}
                          className={inputClass}
                        >
                          <option value="">Select company</option>
                          {companies.map((company) => (
                            <option key={company.id} value={company.id}>
                              {company.name}
                            </option>
                          ))}
                        </select>
                      </label>

                      {runUiVariant === "greenspace" ? (
                        <label className="block">
                          <div className={labelClass}>Vehicle for this run</div>
                          <select
                            value={assignment?.runMeta?.[runKey]?.vehicleId || ""}
                            onChange={(e) =>
                              setRunVehicleMeta(runKey, runCompanyId, e.target.value)
                            }
                            className={inputClass}
                            disabled={!runCompanyId}
                          >
                            <option value="">Select vehicle</option>
                            {runTrucks.map((truck) => (
                              <option key={truck.id} value={truck.id}>
                                {getTruckDisplayLabel(truck)}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row xl:items-center">
                    <button
                      onClick={() => addJobToEnd(runKey)}
                      className="rounded-xl bg-emerald-600 px-3 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700"
                    >
                      Add job to end
                    </button>
                    <button
                      onClick={() => deleteRun(runKey)}
                      className="rounded-xl bg-red-600 px-3 py-2.5 text-sm font-medium text-white transition hover:bg-red-700"
                    >
                      Delete run
                    </button>
                  </div>
                </div>

                {visibleRealIndices.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                    No jobs in this run yet. Use “Add job to end” to create one.
                  </div>
                )}

                {visibleRealIndices.map((realIndex, visIdx) => {
                  const item = jobs[realIndex];
                  const rowIsGreenspace = isGreenspaceRow(item, runCompany);

                  const selectedCustomer = runCustomers.find(
                    (x) => x.id === (item.customerId || ""),
                  );
                  const customerSites = Array.isArray(selectedCustomer?.sites)
                    ? selectedCustomer.sites
                    : [];
                  const selectedSite = customerSites.find(
                    (x) => x.id === (item.siteId || ""),
                  );

                  const isOpen = openJobKeys.has(`${runKey}__${realIndex}`);
                  const serviceLabel = rowIsGreenspace
                    ? runJobTypes.find((jt) => jt.id === item.jobTypeId)?.name || ""
                    : JOB_TYPES.find((jt) => jt.code === item.jobTypeCode)?.name || "";

                  return (
                    <div
                      key={`${runKey}-${realIndex}`}
                      className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
                        <button
                          type="button"
                          onClick={() => toggleJobOpen(runKey, realIndex)}
                          aria-expanded={isOpen}
                          className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        >
                          <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-gray-900 px-2 text-xs font-semibold text-white">
                            {visIdx + 1}
                          </span>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900">
                              Job #{visIdx + 1}
                            </div>
                            <div className="truncate text-xs text-gray-500">
                              {isOpen
                                ? "Edit details for this assigned stop"
                                : [
                                    item.address || "No address yet",
                                    selectedCustomer?.name,
                                    serviceLabel,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                            </div>
                          </div>
                          <svg
                            className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </button>

                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => insertJobAt(runKey, realIndex)}
                            className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium transition hover:bg-gray-100"
                            title="Insert a new job above this one"
                          >
                            Insert above
                          </button>
                          <button
                            onClick={() => insertJobAt(runKey, realIndex + 1)}
                            className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium transition hover:bg-gray-100"
                            title="Insert a new job below this one"
                          >
                            Insert below
                          </button>
                          <button
                            onClick={() => moveJob(runKey, visIdx, "up")}
                            disabled={visIdx === 0}
                            className={`rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium transition ${
                              visIdx === 0
                                ? "cursor-not-allowed opacity-40"
                                : "hover:bg-gray-100"
                            }`}
                            title="Move up"
                          >
                            Move up
                          </button>
                          <button
                            onClick={() => moveJob(runKey, visIdx, "down")}
                            disabled={visIdx === visibleRealIndices.length - 1}
                            className={`rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium transition ${
                              visIdx === visibleRealIndices.length - 1
                                ? "cursor-not-allowed opacity-40"
                                : "hover:bg-gray-100"
                            }`}
                            title="Move down"
                          >
                            Move down
                          </button>
                        </div>
                      </div>

                      {isOpen && (
                      <div className="space-y-4 p-4">
                        <div className={filledCardClass}>
                          <div className="grid gap-4 md:grid-cols-2">
                            <label className="block">
                              <div className={labelClass}>Customer</div>
                              <SearchableSelect
                                className="w-full"
                                value={item.customerId || ""}
                                placeholder="Select customer"
                                searchPlaceholder="Search customer..."
                                emptyText="No customers found."
                                options={runCustomers.map((c) => ({
                                  value: c.id,
                                  label: c.name || "(unnamed customer)",
                                  description: [c.phone, c.email]
                                    .filter(Boolean)
                                    .join(" • "),
                                  searchText: [
                                    c.name,
                                    c.phone,
                                    c.email,
                                    c.address,
                                    ...(Array.isArray(c.sites)
                                      ? c.sites.flatMap((site) => [
                                          site?.label,
                                          site?.address,
                                          getSiteContactName(site),
                                          getSiteContactPhone(site),
                                          getSiteContactEmail(site),
                                        ])
                                      : []),
                                  ]
                                    .filter(Boolean)
                                    .join(" "),
                                }))}
                                onChange={(id) => {
                                  handleChange(
                                    runKey,
                                    realIndex,
                                    "customerId",
                                    id,
                                  );
                                  handleChange(runKey, realIndex, "siteId", "");
                                  const c = runCustomers.find((x) => x.id === id);
                                  if (c) {
                                    const hasSites =
                                      Array.isArray(c.sites) &&
                                      c.sites.length > 0;
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "address",
                                      hasSites ? "" : c.address || "",
                                    );
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "siteLabel",
                                      "",
                                    );
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "siteContact",
                                      hasSites ? "" : c.name || "",
                                    );
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "sitePhone",
                                      hasSites ? "" : c.phone || "",
                                    );
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "siteEmail",
                                      hasSites ? "" : c.email || "",
                                    );
                                  } else {
                                    handleChange(runKey, realIndex, "address", "");
                                    handleChange(runKey, realIndex, "siteLabel", "");
                                    handleChange(runKey, realIndex, "siteContact", "");
                                    handleChange(runKey, realIndex, "sitePhone", "");
                                    handleChange(runKey, realIndex, "siteEmail", "");
                                  }
                                }}
                              />
                            </label>

                            <div className="rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-gray-600">
                              {selectedCustomer ? (
                                <>
                                  Editing job for{" "}
                                  <span className="font-semibold text-gray-900">
                                    {selectedCustomer.name}
                                  </span>
                                </>
                              ) : (
                                "Choose a customer to begin."
                              )}
                            </div>
                          </div>

                          {!!customerSites.length && (
                            <div className="mt-4 grid items-start gap-4 md:grid-cols-4">
                              <label className="block md:col-span-2">
                                <div className={labelClass}>Site</div>
                                <SearchableSelect
                                  className="w-full"
                                  value={item.siteId || ""}
                                  placeholder="Select site"
                                  searchPlaceholder="Search site..."
                                  emptyText="No sites found."
                                  options={customerSites.map((site) => ({
                                    value: site.id,
                                    label: site.label || "(unnamed site)",
                                    description: [
                                      site.address,
                                      getSiteContactName(site),
                                      getSiteContactPhone(site),
                                    ]
                                      .filter(Boolean)
                                      .join(" • "),
                                    searchText: [
                                      site.label,
                                      site.address,
                                      getSiteContactName(site),
                                      getSiteContactPhone(site),
                                      getSiteContactEmail(site),
                                    ]
                                      .filter(Boolean)
                                      .join(" "),
                                  }))}
                                  onChange={(siteId) => {
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "siteId",
                                      siteId,
                                    );
                                    const site = customerSites.find(
                                      (x) => x.id === siteId,
                                    );
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "siteLabel",
                                      site?.label || "",
                                    );
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "siteContact",
                                      getSiteContactName(site) || "",
                                    );
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "sitePhone",
                                      getSiteContactPhone(site) || "",
                                    );
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "siteEmail",
                                      getSiteContactEmail(site) || "",
                                    );
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "address",
                                      site?.address || "",
                                    );
                                  }}
                                />
                              </label>

                              <div className="md:col-span-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-gray-700">
                                {selectedSite ? (
                                  <div className="space-y-1.5">
                                    <div>
                                      <span className="font-medium text-gray-800">
                                        Contact:
                                      </span>{" "}
                                      {getSiteContactName(selectedSite) || "-"}
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-800">
                                        Phone:
                                      </span>{" "}
                                      {getSiteContactPhone(selectedSite) || "-"}
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-800">
                                        Email:
                                      </span>{" "}
                                      {getSiteContactEmail(selectedSite) || "-"}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="italic text-gray-500">
                                    Select a site to auto-fill contact and
                                    address details.
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className={filledCardClass}>
                          <div>
                            <div className={labelClass}>Address</div>
                            {scriptLoaded ? (
                              <Autocomplete
                                onLoad={(auto) => {
                                  autocompleteRefs.current[
                                    `${runKey}-${realIndex}`
                                  ] = auto;
                                }}
                                onPlaceChanged={() => {
                                  const auto =
                                    autocompleteRefs.current[
                                      `${runKey}-${realIndex}`
                                    ];
                                  if (!auto) return;
                                  const place = auto.getPlace();
                                  if (place?.formatted_address) {
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "address",
                                      place.formatted_address,
                                    );
                                  } else if (place?.name) {
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "address",
                                      place.name,
                                    );
                                  }
                                }}
                              >
                                <input
                                  type="text"
                                  placeholder="Address"
                                  value={item.address || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "address",
                                      e.target.value,
                                    )
                                  }
                                  className={inputClass}
                                  required
                                />
                              </Autocomplete>
                            ) : (
                              <input
                                type="text"
                                placeholder="Address"
                                value={item.address || ""}
                                onChange={(e) =>
                                  handleChange(
                                    runKey,
                                    realIndex,
                                    "address",
                                    e.target.value,
                                  )
                                }
                                className={inputClass}
                                required
                              />
                            )}
                          </div>

                          <div className="mt-4">
                            <div className={labelClass}>
                              Description / instructions
                            </div>
                            <textarea
                              placeholder="Description / instructions for this job"
                              value={item.description || ""}
                              onChange={(e) =>
                                handleChange(
                                  runKey,
                                  realIndex,
                                  "description",
                                  e.target.value,
                                )
                              }
                              className={`${inputClass} min-h-[110px] resize-y`}
                            />
                          </div>
                        </div>

                        <div className={filledCardClass}>
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                            <label className="block">
                              <div className={labelClass}>Services</div>

                              {rowIsGreenspace ? (
                                <select
                                  value={item.jobTypeId || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "jobTypeId",
                                      e.target.value,
                                    )
                                  }
                                  className={inputClass}
                                >
                                  <option value="">No Services</option>
                                  {runJobTypes.map((jt) => (
                                    <option key={jt.id} value={jt.id}>
                                      {jt.name}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <select
                                  value={item.jobTypeCode || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      runKey,
                                      realIndex,
                                      "jobTypeCode",
                                      e.target.value,
                                    )
                                  }
                                  className={inputClass}
                                >
                                  <option value="">No Services</option>
                                  {JOB_TYPES.map((jt) => (
                                    <option key={jt.code} value={jt.code}>
                                      {jt.name}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </label>

                            <label className="block">
                              <div className={labelClass}>ETA</div>
                              <input
                                placeholder="ETA"
                                type="time"
                                value={item.eta || ""}
                                onChange={(e) =>
                                  handleChange(
                                    runKey,
                                    realIndex,
                                    "eta",
                                    e.target.value,
                                  )
                                }
                                className={inputClass}
                              />
                            </label>

                            <label className="block">
                              <div className={labelClass}>PO number</div>
                              <input
                                placeholder="PO number"
                                value={item.poNumber || ""}
                                onChange={(e) =>
                                  handleChange(
                                    runKey,
                                    realIndex,
                                    "poNumber",
                                    e.target.value,
                                  )
                                }
                                className={inputClass}
                              />
                            </label>

                            <label className="block">
                              <div className={labelClass}>
                                EPA Waste Tracker
                              </div>
                              <input
                                placeholder="EPA Waste Tracker"
                                value={item.epaWasteTracker || ""}
                                onChange={(e) =>
                                  handleChange(
                                    runKey,
                                    realIndex,
                                    "epaWasteTracker",
                                    e.target.value,
                                  )
                                }
                                className={inputClass}
                              />
                            </label>

                            <label className="block">
                              <div className={labelClass}>Site contact</div>
                              <input
                                placeholder="Site contact"
                                value={item.siteContact || ""}
                                onChange={(e) =>
                                  handleChange(
                                    runKey,
                                    realIndex,
                                    "siteContact",
                                    e.target.value,
                                  )
                                }
                                className={inputClass}
                              />
                            </label>

                            <label className="block">
                              <div className={labelClass}>Site phone</div>
                              <input
                                placeholder="Site phone"
                                value={item.sitePhone || ""}
                                onChange={(e) =>
                                  handleChange(
                                    runKey,
                                    realIndex,
                                    "sitePhone",
                                    e.target.value,
                                  )
                                }
                                className={inputClass}
                              />
                            </label>

                            <label className="block">
                              <div className={labelClass}>Customer email</div>
                              <input
                                type="email"
                                placeholder="Customer email"
                                value={item.siteEmail || ""}
                                onChange={(e) =>
                                  handleChange(
                                    runKey,
                                    realIndex,
                                    "siteEmail",
                                    e.target.value,
                                  )
                                }
                                className={inputClass}
                              />
                            </label>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 pt-1">
                          <div className="text-xs text-gray-500">
                            Changes here will update this job in the driver
                            dashboard.
                          </div>
                          <button
                            onClick={() => deleteJob(runKey, realIndex)}
                            className="rounded-xl bg-red-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-red-700"
                          >
                            Delete job
                          </button>
                        </div>
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          <div className="flex justify-end gap-2">
            <button
              onClick={addRun}
              className="mt-2 rounded-xl bg-gray-900 px-6 py-2.5 text-sm font-medium text-white transition hover:bg-black"
            >
              Add run
            </button>
            <button
              onClick={saveChanges}
              className="mt-2 rounded-xl bg-emerald-600 px-6 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700"
            >
              Save changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
